import { Attrs, EventListeners, MinzeElement, Reactive } from 'minze'

import flatpickr from 'flatpickr'
import { Font } from '../../../constants'
import { Callback } from '../../../modules/callback'
import { Locale } from '../../../modules/locale'
import MainModule from '../../../modules/main'
import { parseInlineStyle } from '../../../modules/utils'

export class LvcTimepicker extends MinzeElement {
  reactive: Reactive = [['value', '', true]]

  attrs: Attrs = [
    ['disabled', false],
    ['placeholder', ''],
    ['height', '48px'],
    ['width', '100%'],
    ['error', false],
    'name',
    'id',
    ['default-value', ''],
    ['date-format', 'YYYY-MM-DD'],
    'label',
    ['padding', '12px 16px'],
    ['fontSize', '14px'],
    ['marginTop', ''],
    ['clear-button-enabled', false],
    'placeholder-color',
    ['inner-style', ''],
    ['onChange'],
    ['preset', ''],
    ['position', 'auto'],
    ['background', ''],
  ]

  static observedAttributes = [
    'inner-style',
    'placeholder',
    'height',
    'name',
    'id',
    'label',
    'date-format',
    'default-value',
    'padding',
    'error',
    'onChange',
  ]

  fieldType: string = 'date'
  internalValue: string = ''
  flatpickrInstance: any
  isCalendarOpen: boolean = false

  onReady() {
    const language = Locale.language
    const injectedElement = this.select('#flatpickr-input') as HTMLElement | undefined

    this.initializeFlatpickr(
      this.internalValue as string,
      (this.defaultValue as string) || (this.value as string),
      injectedElement
    )
    setTimeout(() => {
      this.rerender()
    }, 0)
  }

  initializeFlatpickr(internalValue: string, defaultValue: string, injectedElement?: HTMLElement) {
    let container = MainModule.getInstance().getDatepickerContainer() as HTMLElement | null
    if (!injectedElement) {
      return
    }
    if (this.flatpickrInstance) {
      // do nothing for the moment
    } else {
      this.flatpickrInstance = flatpickr(injectedElement as any, {
        enableTime: true,
        dateFormat: 'H:i',
        noCalendar: true,
        time_24hr: true,
        position: this.position ? (this.position as any) : 'auto',
        appendTo: container || undefined,
        clickOpens: true,
        onChange: (selectedTimes: Date[], dateStr: string, instance: any) => {
          this.value = dateStr

          if (selectedTimes.length < 0) {
            this.value = ''
          }
          this.cast('change', { value: this.value })
          Callback.call(this.onChange as string, this.value)
        },
        onOpen: (selectedTimes: Date[], dateStr: string, instance: any) => {
          this.isCalendarOpen = true
          this.rerender()
        },
        onClose: (selectedTimes: Date[], dateStr: string, instance: any) => {
          this.isCalendarOpen = false
          this.rerender()
        },
      })
    }
  }

  onRender() {
    const injectedElement = this.select('#flatpickr-input') as HTMLElement | undefined

    this.initializeFlatpickr(
      this.internalValue as string,
      (this.defaultValue as string) || (this.value as string) || '',
      injectedElement
    )
  }

  onAttributeChange = (name: string, oldValue: string, newValue: string) => {
    if (name === 'date-format') {
      this.dateFormat = newValue
    }

    if (name === 'placeholder') {
      this.placeholder = newValue
    }

    if ((name === 'default-value' || name === 'value') && oldValue !== newValue) {
      let valueToSet = newValue
      let oldSetValue = oldValue

      if (newValue === null) {
        valueToSet = ''
      }

      if (oldValue === null) {
        oldSetValue = ''
      }

      if (oldValue === null) {
        oldValue = ''
      }

      if (oldSetValue !== valueToSet) {
        this.value = valueToSet
        // this.interalValue = valueToSet
        //only value can update input directly if changed from outside
        //or else you risk forever loop
        const currentElement = this.select(`input`)
        if (currentElement as any) {
          ;(currentElement as any).value = valueToSet
        }
      }
    }

    this.paddingValue = this.padding ? this.padding : '12px 16px'
    this.fontSizeValue = this.fontSize ? this.fontSize : '14px'
    this.heightValue = this.height ? this.height : '48px'
  }

  onBlur(e: Event) {
    setTimeout(() => {
      this.rerender()
    }, 0)
  }

  html = () => {
    let fieldType = this.fieldType
    let showError = typeof this.error === 'string' && this.error.length > 0
    let val = ''

    return `
    

        ${this.label ? `<label for="${this.id}">${this.label}</label>` : ''}
        <div class="input-container">
          <input
            data-input
            ${this.disabled ? `disabled` : ''}
            type="text"
            data-main-input="true"
            ${this.placeholder ? `placeholder="${this.placeholder}"` : ``}
            name="${this.name}"
            id="flatpickr-input"
            pattern="${this.pattern || ''}"
            ${val ? `value="${val}"` : ''}
            class="input ${this.error ? 'error' : ''}"
            readonly="readonly"
          >

        </div>



      `
  }

  css = () => {
    return `
    :host {
      position: relative;
      box-sizing: border-box;
      display: block;
      border-radius: 99px;
    }

    .icon{
      display:flex;
      position: absolute;
      right: 16px;
      cursor: pointer;
    }
   
    #flatpickr-input:focus-visible {
      outline: unset;
    }

    .input-container{
        display: flex;
        align-items: center;
        
    }


    .button {
      cursor:pointer;
      height: 48px;
      display: flex;
      align-items: center;
      background-color: #fff;
      padding: 0 5px;

    }

    #flatpickr-input::placeholder {
        text-align: center;
    }

    #flatpickr-input {
        width: ${this.width};
        border: none;
        border-radius: 99px;
        ${this.heightValue ? `height: ${this.heightValue};` : ''}
        ${this.fontSizeValue ? `font-size: ${this.fontSizeValue};` : ''}
        ${this.paddingValue ? `padding: ${this.paddingValue};` : ''}
        ${this.isCalendarOpen ? `background: #BFF0FF;` : `background: ${this.background || '#BFF0FF'};`}
        color: #1E007D;
        ${this.marginTop ? `margin-top: ${this.marginTop};` : ''}
        border-radius: 99px;
        cursor: pointer;
        flex: 1;
        text-align: center;
        display: flex;
        justify-content: space-between;
        align-items: center;
    }
    
    ${
      this.placeholderColor
        ? `
      ::placeholder {
        color: ${this.placeholderColor};
        opacity: 1;
        font-size: 16px;
        font-weight: ${Font.weight.medium};

      }
      
      :-ms-input-placeholder {
        color: ${this.placeholderColor};
        font-size: 16px;
        font-weight: ${Font.weight.medium};
      }
      
      ::-ms-input-placeholder {
        color: ${this.placeholderColor};
        font-size: 16px;
        font-weight: ${Font.weight.medium};
      }
    `
        : ``
    }

    label {
      color:  #1E007D;
      font-size: 14px;
      line-height: 17px;
      font-weight: ${Font.weight.bold};
    }

    .error {
      background: #FFEBD7;
    }

    .flatpickr-months .flatpickr-month {
      display: flex;
      align-items: center;
    }
    
    .flatpickr-months .flatpickr-prev-month, .flatpickr-months .flatpickr-next-month {
      padding: 0 10px;
      display: flex;
      align-items: center;
    }
    
    .flatpickr-current-month {
      width: 100%;
      position: static;
      padding: 0;
      height: auto;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    
    .flatpickr-current-month span.cur-month:hover, .numInputWrapper:hover {
      background: none;
    }
    
    .flatpickr-current-month span.cur-month, .flatpickr-current-month input.cur-year {
      font-weight: ${Font.weight.medium};
      font-size: 17px;
      line-height: 120%;
      color: #1E007D;
      text-transform: capitalize;
    }
    
    .flatpickr-current-month .numInputWrapper {
      width: 50px;
      display: flex;
      align-items: center;
    }
    
    .numInputWrapper span.arrowUp, .numInputWrapper span.arrowDown {
      display: none;
    }
    
    .dayContainer, .flatpickr-days, .flatpickr-calendar {
      width: 336px;
      box-shadow: 0px 4px 16px 0px rgba(15, 0, 62, 0.15);
    }

    .flatpickr-wrapper {
      width:100%;
    }
    
    .flatpickr-days {
      margin-top: 12px;
    }
    
    .dayContainer {
      max-width: 336px;
      min-width: 336px;
      row-gap: 4px;
      height: calc(5 * 48px + 16px);
      overflow: hidden;
    }
    
    .flatpickr-day {
      border: none;
      margin: 0;
      max-width: none;
      width: 48px;
      height: 48px;
      display: flex;
      justify-content: center;
      align-items: center;
      font-weight: ${Font.weight.medium};
      font-size: 14px;
      line-height: 16px;
      color: #1E007D;
    }
    
    .flatpickr-day:hover, .flatpickr-day.today:hover {
      background: #1E007D;
      border-radius: 8px;
      color: #FFFFFF;
    }
    
    .flatpickr-day.today.selected:hover, .flatpickr-day.today.endRange:hover, .flatpickr-day.today.startRange:hover {
      color: #FFFFFF;
      background: #1E007D;
    }
    
    .flatpickr-day.selected.startRange + .endRange:not(:nth-child(7n+1)),
    .flatpickr-day.startRange.startRange + .endRange:not(:nth-child(7n+1)),
    .flatpickr-day.endRange.startRange + .endRange:not(:nth-child(7n+1)) {
      box-shadow: none;
    }
    
    .flatpickr-day.inRange, .flatpickr-day.today.inRange {
      background: #BFF0FF;
      box-shadow: none;
    }
    
    .flatpickr-day.inRange:hover {
      border-radius: 0;
    }
    
    .rangeMode .flatpickr-day {
      margin-top: 0;
    }
    
    .flatpickr-day.selected {
      background: #1E007D;
    }
    
    .flatpickr-day.startRange, .flatpickr-day.endRange, .flatpickr-day.startRange:hover, .flatpickr-day.endRange:hover {
      background: #1E007D;
    }
    
    .flatpickr-day.selected,
    .flatpickr-day.startRange,
    .flatpickr-day.endRange,
    .flatpickr-day.selected.inRange,
    .flatpickr-day.startRange.inRange,
    .flatpickr-day.endRange.inRange,
    .flatpickr-day.selected:focus,
    .flatpickr-day.startRange:focus,
    .flatpickr-day.endRange:focus,
    .flatpickr-day.selected:hover,
    .flatpickr-day.startRange:hover,
    .flatpickr-day.endRange:hover,
    .flatpickr-day.selected.prevMonthDay,
    .flatpickr-day.startRange.prevMonthDay,
    .flatpickr-day.endRange.prevMonthDay,
    .flatpickr-day.selected.nextMonthDay,
    .flatpickr-day.startRange.nextMonthDay,
    .flatpickr-day.endRange.nextMonthDay {
      background: #1E007D;
    }
    
    .flatpickr-day.selected, .flatpickr-day.startRange {
    }
    
    .flatpickr-day.selected.startRange, .flatpickr-day.startRange.startRange, .flatpickr-day.endRange.startRange {
      border-radius: 8px 0px 0px 8px;
    }
    
    .flatpickr-day.selected.endRange, .flatpickr-day.startRange.endRange, .flatpickr-day.endRange.endRange {
      border-radius: 0px 8px 8px 0px;
    }
    
    .flatpickr-day.flatpickr-disabled, 
    .flatpickr-day.flatpickr-disabled:hover, 
    .flatpickr-day.prevMonthDay, 
    .flatpickr-day.nextMonthDay, 
    .flatpickr-day.notAllowed, 
    .flatpickr-day.notAllowed.prevMonthDay, 
    .flatpickr-day.notAllowed.nextMonthDay {
      font-weight: ${Font.weight.medium};
      font-size: 14px;
      line-height: 16px;
      color: rgba(30, 0, 125, 0.3);
    }
    
    .flatpickr-weekdays {
      height: auto;
      padding: 16px 0 12px 0;
    }
    
    span.flatpickr-weekday {
      font-weight: ${Font.weight.medium};
      font-size: 12px;
      line-height: 120%;
      color: #1E007D;
    }

    .drawer-content .flatpickr-calendar {
      background: transparent;
      box-shadow: 0px 4px 16px 0px rgba(15, 0, 62, 0.15);;
    }


    // dropdown styles 


    .overlay {
      position: fixed;
      top: 0px;
      left: 0px;
      bottom: 0px;
      right: 0px;
      width: 100%;
      height: 100%;
      z-index: 1;
      display: none;
    }
    .overlay.show {
      display: block;
    }

    .header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      width: 100%;
      height: 48px;
      border: none;
      outline: none;
      padding: 0 16px;
      box-sizing: border-box;
      font-family: ${Font.family};
      font-size: 14px;
      font-style: normal;
      font-weight: ${Font.weight.medium};
      line-height: 150%; /* 21px */
      color: var(--Minuit, #1E007D);
      background: var(--Ciel, #BFF0FF);

    }


    input[name="filter"] {
      width: 100%;
      height: 48px;
      border: none;
      outline: none;
      padding: 0 0;
      box-sizing: border-box;
      font-family: ${Font.family};
      font-size: 14px;
      font-style: normal;
      font-weight: ${Font.weight.medium};
      line-height: 150%; /* 21px */
      color: var(--Minuit, #1E007D);
      background: transparent;
    }

    .title {
      color: var(--Minuit, #1E007D);
      font-variant-numeric: lining-nums tabular-nums;
      font-feature-settings: 'clig' off, 'liga' off;

      /* Body 3 */
      font-family: ${Font.family};
      font-size: 14px;
      font-style: normal;
      font-weight: ${Font.weight.medium};
      line-height: 150%; /* 21px */
    }

    .input {
      display: flex;
      height: 48px;
      flex-direction: column;
      justify-content: center;
      align-items: flex-start;
      flex-shrink: 0;
      align-self: stretch;
      position: relative;
    }

 
    ${parseInlineStyle(this.innerStyle as string)}
  `
  }

  clearInput = () => {
    if (this.value === '') return

    this.value = ''

    const currentElement = this.select(`input`)
    if (currentElement as any) {
      ;(currentElement as any).value = ''
    }
    this.rerender()
  }

  onIconClick(e: Event) {
    if (this.flatpickrInstance) {
      this.flatpickrInstance.open()
    }
  }

  eventListeners: EventListeners = [
    ['input', 'blur', this.onBlur.bind(this)],
    ['.input-button', 'click', this.onIconClick.bind(this)],
    ['.clear-input-button', 'click', this.clearInput.bind(this)],
  ]
}
